import axios from 'axios';
import { CURR_API } from '@/constants/apis';
import { REQUEST_MARKER, isOurOwnOrigin } from '@/api/requestMarker';
import { refusalMessage } from '@/api/refusals';
import { isFileUid } from '@/utils/fileUrl';

/**
 * keyon's resumable video upload (keyon/video_upload.php): the policy, and the
 * five calls of an upload - start, chunk, status, complete, discard.
 *
 * It has an axios instance of its own and does not use the global one. The
 * global instance carries the handler that sends a person whose MFA row lapsed
 * to the code page (mfaRequired.js), which would throw away the message being
 * composed while a video is uploading; an upload parks on that answer instead
 * (videoUploader.js) and asks for the code in another window. It also carries
 * the request marker as an interceptor, so the marker is set here on each
 * request, as discardUnclaimedCredentialUpload does for its fetch.
 *
 * Every call answers an object and none throws: {ok:true, status, data}, or
 * {ok:false, status, code, message, reason, retryAfter, nextOffset, network,
 * cancelled}. A request that never got an answer (no network, a timeout of the
 * caller's own, a cancel) has status 0 and network true.
 */

export const VIDEO_UPLOAD_URL = `${CURR_API}/video_upload.php`;

const url = (action, query = '') => `${VIDEO_UPLOAD_URL}?action=${action}${query}`;

const jsonHeaders = () => ({ 'Content-Type': 'application/json', [REQUEST_MARKER]: '1' });

// No total timeout: a chunk of 8 MiB on a slow link legitimately takes a while,
// and a stalled one is found by watching for progress (videoUploader.js).
export function createVideoUploadClient() {
  return axios.create({ timeout: 0 });
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function wholeNumber(value) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) return value;
  if (typeof value === 'string' && /^[0-9]{1,15}$/.test(value)) return Number(value);
  return null;
}

/** An axios response as the answer this file gives. Any status is an answer. */
function interpret(res) {
  const status = res && typeof res.status === 'number' ? res.status : 0;
  const data = res ? res.data : undefined;
  if (status >= 200 && status < 300 && isObject(data)) return { ok: true, status, data };
  const body = isObject(data) ? data : {};
  const headers = (res && res.headers) || {};
  return {
    ok: false,
    status,
    code: typeof body.code === 'string' ? body.code : null,
    // keyon's sentence for a code it owns; anything else is the caller's own words.
    message: refusalMessage(body),
    reason: typeof body.reason === 'string' ? body.reason : null,
    retryAfter: wholeNumber(headers['retry-after']) ?? wholeNumber(body.retry_after),
    nextOffset: wholeNumber(body.next_offset),
    network: false,
    cancelled: false,
  };
}

function noAnswer(error) {
  return {
    ok: false,
    status: 0,
    code: null,
    message: null,
    reason: null,
    retryAfter: null,
    nextOffset: null,
    network: true,
    cancelled: axios.isCancel(error),
  };
}

async function send(call) {
  try {
    return interpret(await call());
  } catch (error) {
    return noAnswer(error);
  }
}

/**
 * Whether this environment takes video from a message, and with what limits:
 * the config answer as keyon sent it, or null when there is none to trust (a
 * keyon from before video answers 404, a lapsed MFA row 401, and a proxy's page
 * is not JSON). A null is the picker exactly as it was.
 * @returns {Promise<object|null>}
 */
export async function loadVideoPolicy({ client = createVideoUploadClient() } = {}) {
  const result = await send(() => client.get(url('config'), { validateStatus: () => true, timeout: 10000 }));
  if (!result.ok) return null;
  const config = result.data;
  if (typeof config.enabled !== 'boolean') return null;
  if (config.enabled) {
    const usable = Number.isFinite(config.max_bytes) && config.max_bytes > 0 &&
      Array.isArray(config.extensions) && Array.isArray(config.mime_types);
    if (!usable) return null;
  }
  return config;
}

/**
 * Releases an upload the person walked away from: a removed chip, a closed
 * composer. keyon abandons their own video while nothing has referenced it and
 * leaves a bound one alone. Best-effort and silent, like
 * discardUnclaimedCredentialUpload: fetch with `keepalive` so it finishes when
 * the page is going away, and it stays clear of axios and its MFA handler.
 * @returns {Promise<boolean>} whether keyon took the request
 */
export async function discardVideoUpload(uploadId, deps = {}) {
  if (!isFileUid(uploadId)) return false;
  const fetchImpl = deps.fetch || (typeof fetch === 'function' ? (...args) => fetch(...args) : null);
  if (!fetchImpl) return false;
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (isOurOwnOrigin(VIDEO_UPLOAD_URL)) headers[REQUEST_MARKER] = '1';
    const res = await fetchImpl(url('discard'), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers,
      body: JSON.stringify({ upload_id: uploadId }),
    });
    if (!res || !res.ok) return false;
    const data = await res.json();
    return isObject(data) && data.ok === true;
  } catch (_) {
    return false;
  }
}

/**
 * The calls of one upload over one client. `client` is anything with axios's
 * get and post, and `fetch` is what discard goes out on; both are injectable.
 */
export function createVideoUploadApi({ client = createVideoUploadClient(), fetch: fetchImpl } = {}) {
  const base = { validateStatus: () => true, timeout: 0 };
  const withToken = (cancelToken) => (cancelToken ? { cancelToken } : {});
  return {
    config: () => loadVideoPolicy({ client }),

    // A note's video is about a patient: patientId goes up as patient_id, which keyon
    // resolves inside the session's account and authorizes; it is never the owner.
    start({ domain, patientId, filename, size, chunkSize, resumeKey }, { cancelToken } = {}) {
      const body = { domain, filename, size, chunk_size: chunkSize, resume_key: resumeKey };
      if (patientId !== undefined && patientId !== null && patientId !== '') body.patient_id = patientId;
      return send(() => client.post(url('start'), body, { ...base, ...withToken(cancelToken), headers: jsonHeaders() }));
    },

    // multipart: the browser writes the boundary, so there is no Content-Type here.
    chunk({ uploadId, offset, sha256, blob }, { onProgress, cancelToken } = {}) {
      const form = new FormData();
      form.append('upload_id', uploadId);
      form.append('offset', String(offset));
      form.append('sha256', sha256);
      form.append('chunk', blob, 'chunk');
      return send(() => client.post(url('chunk'), form, {
        ...base,
        ...withToken(cancelToken),
        headers: { [REQUEST_MARKER]: '1' },
        onUploadProgress: onProgress,
      }));
    },

    status(uploadId, { cancelToken } = {}) {
      return send(() => client.get(url('status', `&upload_id=${encodeURIComponent(uploadId)}`), { ...base, ...withToken(cancelToken) }));
    },

    complete(uploadId, { cancelToken } = {}) {
      return send(() => client.post(url('complete'), { upload_id: uploadId }, { ...base, ...withToken(cancelToken), headers: jsonHeaders() }));
    },

    discard: (uploadId) => discardVideoUpload(uploadId, { fetch: fetchImpl }),
  };
}
